'use client'

import Image from 'next/image'
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { Dialog } from '@acko/dialog'
import { Button } from '@acko/button'
import { TextInput } from '@acko/text-input'
import { Typography } from '@acko/typography'
import { pageEvent, trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import {
  ABHA_HERO_EXISTING_ABHA_EMPTY_STEP_DATA,
  ABHA_HERO_EXISTING_ABHA_OTP_STEP_DATA,
  ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA,
  ABHA_HERO_EXISTING_ABHA_PREPARE_STEP_DATA,
  ABHA_HERO_MODAL_PREPARE_DURATION_MS,
  ABHA_HERO_MODAL_RESEND_OTP_SECONDS,
  ABHA_HERO_OTP_ERROR_MESSAGE,
  ABHA_HERO_PHONE_ERROR_MESSAGE,
  ABHA_HERO_PHONE_MAX_LENGTH,
  ABHA_HERO_SUCCESS_APP_LINK,
  ABHA_HERO_WIDGET_DEFAULT_DATA,
  ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA,
} from '@components/NewProduct/constants'
import { AbhaHeroModalCloseButton } from '@components/NewProduct/AbhaHeroWidget/AbhaHeroModalCloseButton'
import { ModalDisclaimer } from '@components/NewProduct/AbhaHeroWidget/ModalDisclaimer'
import { AbhaHeroOtpStep } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroOtpStep'
import { AbhaHeroPrepareStep } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroPrepareStep'
import { AbhaHeroExistingAbhaSuccessStep } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroExistingAbhaSuccessStep'
import { resolveAbhaHeroStepData } from '@components/NewProduct/AbhaHeroWidget/steps/abhaHeroStepData'
import { AbhaJourneyNodeId } from '@components/NewProduct/AbhaHeroWidget/enums'
import { downloadAbhaCardAsset } from '@components/NewProduct/AbhaHeroWidget/abhaCardDownload'
import {
  ABHA_ANALYTICS_ACTION_TYPES,
  ABHA_ANALYTICS_EVENTS,
  ABHA_ANALYTICS_JOURNEYS,
  ABHA_ANALYTICS_PAGE_TYPES,
  ABHA_ANALYTICS_VARIANTS,
  buildAbhaAnalyticsProps,
  buildAbhaPageViewProps,
  resolveAbhaSuccessVariantName,
  ABHA_STATE,
} from '@components/NewProduct/AbhaHeroWidget/abhaHeroAnalytics'
import { sanitizeAbhaPhoneNumber } from '@helpers/abhaPhoneHelper'
import { ABHA_OTP_LENGTH } from '@helpers/abhaWidgetHelper'
import { isValidPhoneNumber } from '@utils/utils'
import { ABHA_SEO_WIDGET } from '@components/Product/ProductWidget/constants'
import { fetchDeepLink } from '@services/fetchDeepLink'
import { useBodyScrollLock } from '@components/NewProduct/AbhaHeroWidget/useBodyScrollLock'
import {
  isAbhaOtpInvalidError,
  resendExistingAbhaLoginOtp,
  sanitizeAbhaErrorMessage,
  searchExistingAbhaAccounts,
  selectExistingAbhaAccount,
  startExistingAbhaSearchJourney,
  verifyExistingAbhaLoginOtp,
  type AbhaMemberListDetail,
  type AbhaSessionState,
  type ExistingAbhaSearchResult,
} from '@helpers/abhaJourneyHelper'
import './abhaHeroModal.css'
import './abhaHeroSuccess.css'
import './abhaHeroExistingAbha.css'

export type AbhaHeroExistingAbhaModalProps = {
  open: boolean
  modalOptions?: Record<string, any>
  searchParams?: Record<string, string>
  segmentEventCommonProperties?: Record<string, unknown>
  onClose: () => void
  onCreateAbha?: () => void
  onFetchAbha?: (result: ExistingAbhaSearchResult & { phone: string }) => void
  preview?: {
    step: 'phone' | 'prepare' | 'accounts' | 'empty' | 'otp' | 'verified'
    viewport: 'mobile' | 'desktop'
    phone: string
    session?: AbhaSessionState
  }
}

type ExistingAbhaPhoneStepContent = {
  title: string
  description: string
  inputLabel: string
  inputPlaceholder: string
  ctaText: string
  disclaimerText: string
  disclaimerIconSrc: string
}

const MOBILE_BREAKPOINT = 1024
const EXISTING_ABHA_MODAL_STEP = {
  Phone: 'phone',
  Prepare: 'prepare',
  Accounts: 'accounts',
  Empty: 'empty',
} as const

type ExistingAbhaModalStep = (typeof EXISTING_ABHA_MODAL_STEP)[keyof typeof EXISTING_ABHA_MODAL_STEP]

const wait = (durationMs: number) =>
  new Promise(resolve => {
    window.setTimeout(resolve, durationMs)
  })

const getExistingAbhaPhoneDefaults = (isMobile: boolean): ExistingAbhaPhoneStepContent => ({
  title: ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.TITLE,
  description: isMobile
    ? ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.MOBILE_DESCRIPTION
    : ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.DESCRIPTION,
  inputLabel: ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.INPUT_LABEL,
  inputPlaceholder: isMobile
    ? ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.MOBILE_PLACEHOLDER
    : ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.PLACEHOLDER,
  ctaText: isMobile
    ? ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.MOBILE_CTA_TEXT
    : ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.CTA_TEXT,
  disclaimerText: isMobile
    ? ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.MOBILE_DISCLAIMER_TEXT
    : ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.DISCLAIMER_TEXT,
  disclaimerIconSrc: ABHA_HERO_EXISTING_ABHA_PHONE_STEP_DATA.DISCLAIMER_ICON_SRC,
})

export const AbhaHeroExistingAbhaModal = ({
  open,
  modalOptions = {},
  searchParams = {},
  segmentEventCommonProperties = {},
  onClose,
  onCreateAbha,
  onFetchAbha,
  preview,
}: AbhaHeroExistingAbhaModalProps) => {
  const requestIdRef = useRef(0)
  const firedPageEventsRef = useRef<Set<string>>(new Set())
  const [isMobile, setIsMobile] = useState(false)
  const [step, setStep] = useState<ExistingAbhaModalStep>(EXISTING_ABHA_MODAL_STEP.Phone)
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [session, setSession] = useState<AbhaSessionState | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [actionError, setActionError] = useState('')
  const [isOtpOpen, setIsOtpOpen] = useState(false)
  const [selectedAccountIndex, setSelectedAccountIndex] = useState<number | null>(null)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState('')
  const [otpSubmitError, setOtpSubmitError] = useState('')
  const [resendCountdown, setResendCountdown] = useState(ABHA_HERO_MODAL_RESEND_OTP_SECONDS)
  const [appDownloadLink, setAppDownloadLink] = useState(ABHA_HERO_SUCCESS_APP_LINK)

  useBodyScrollLock(open)

  useEffect(() => {
    if (preview) {
      setIsMobile(preview.viewport === 'mobile')
      return undefined
    }

    const checkViewport = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => window.removeEventListener('resize', checkViewport)
  }, [preview])

  useEffect(() => {
    if (!open || !preview) return

    setPhone(preview.phone)
    setPhoneError('')
    setSession(preview.session ?? null)
    setActionError('')
    setOtp('')
    setOtpError('')
    setOtpSubmitError('')
    setSelectedAccountIndex(
      preview.step === 'otp' ? Number(preview.session?.abha_member_list_details?.[0]?.account_index) : null
    )
    setIsOtpOpen(preview.step === 'otp')

    if (preview.step === 'prepare') {
      setStep(EXISTING_ABHA_MODAL_STEP.Prepare)
      return
    }

    if (preview.step === 'empty') {
      setStep(EXISTING_ABHA_MODAL_STEP.Empty)
      return
    }

    if (preview.step === 'accounts' || preview.step === 'otp' || preview.step === 'verified') {
      setStep(EXISTING_ABHA_MODAL_STEP.Accounts)
      return
    }

    setStep(EXISTING_ABHA_MODAL_STEP.Phone)
  }, [open, preview])

  useEffect(() => {
    if (!isOtpOpen || resendCountdown <= 0) return undefined

    const timerId = window.setTimeout(() => {
      setResendCountdown(currentValue => Math.max(0, currentValue - 1))
    }, 1000)

    return () => window.clearTimeout(timerId)
  }, [isOtpOpen, resendCountdown])

  useEffect(() => {
    if (!open) {
      requestIdRef.current += 1
      setStep(EXISTING_ABHA_MODAL_STEP.Phone)
      setPhone('')
      setPhoneError('')
      setSession(null)
      setIsSubmitting(false)
      setActionError('')
      setIsOtpOpen(false)
      setSelectedAccountIndex(null)
      setOtp('')
      setOtpError('')
      setOtpSubmitError('')
      setResendCountdown(ABHA_HERO_MODAL_RESEND_OTP_SECONDS)
      setAppDownloadLink(ABHA_HERO_SUCCESS_APP_LINK)
    }
  }, [open])

  const stepData = useMemo(
    () => resolveAbhaHeroStepData(modalOptions?.existingAbhaStepData, isMobile, getExistingAbhaPhoneDefaults(isMobile)),
    [isMobile, modalOptions?.existingAbhaStepData]
  )
  const successData = modalOptions?.existingAbhaSuccessStepData ?? modalOptions?.successStepData
  const emptyData = resolveAbhaHeroStepData(modalOptions?.existingAbhaEmptyStepData, isMobile, {
    title: ABHA_HERO_EXISTING_ABHA_EMPTY_STEP_DATA.TITLE,
    primaryCtaText: ABHA_HERO_EXISTING_ABHA_EMPTY_STEP_DATA.PRIMARY_CTA_TEXT,
    secondaryCtaText: ABHA_HERO_EXISTING_ABHA_EMPTY_STEP_DATA.SECONDARY_CTA_TEXT,
    imageSrc: ABHA_HERO_EXISTING_ABHA_EMPTY_STEP_DATA.IMAGE_SRC,
  })
  const sanitizedPhone = sanitizeAbhaPhoneNumber(phone)
  const isFetchEnabled = isValidPhoneNumber(sanitizedPhone)
  const isVerifyOtpEnabled = otp.length === ABHA_OTP_LENGTH && !isSubmitting
  const isSuccessStep = step === EXISTING_ABHA_MODAL_STEP.Accounts
  const isEmptyStep = step === EXISTING_ABHA_MODAL_STEP.Empty
  const cardCount = session?.abha_member_list_details?.length ?? 0
  const successCurrentState = session?.abha_member_list_details?.some(member => Boolean(member?.token))
    ? ABHA_STATE.DOWNLOAD_ABHA
    : ABHA_STATE.VIEW_ABHA
  const successVariantName =
    successData?.variantName ?? modalOptions?.variantName ?? resolveAbhaSuccessVariantName(successData)
  const successCtaAnalyticsProps =
    !preview && successVariantName === 'hospital_bill'
      ? buildAbhaAnalyticsProps({
          action_type: ABHA_ANALYTICS_ACTION_TYPES.REDIRECTION,
          cta_text: '',
          current_state: ABHA_STATE.DOWNLOAD_ABHA,
          journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
          page_type:
            successData?.pageType ?? modalOptions?.pageType ?? segmentEventCommonProperties?.page_type ?? 'seo',
          platform: isMobile ? 'mweb' : 'web',
          variant_name: successVariantName,
        })
      : undefined
  const dialogClassName = [
    'abha-hero-modal',
    'abha-hero-existing-modal',
    step === EXISTING_ABHA_MODAL_STEP.Prepare ? 'abha-hero-existing-modal--prepare' : '',
    isSuccessStep ? 'abha-hero-modal--success abha-hero-existing-success-modal' : '',
    isEmptyStep ? 'abha-hero-existing-empty-modal' : '',
  ]
    .filter(Boolean)
    .join(' ')

  useEffect(() => {
    if (!open || preview) {
      if (!open) firedPageEventsRef.current.clear()
      return
    }

    if (step === EXISTING_ABHA_MODAL_STEP.Phone) {
      const eventKey = ABHA_ANALYTICS_PAGE_TYPES.ENTER_PHONE_NUMBER_PAGE

      if (!firedPageEventsRef.current.has(eventKey)) {
        firedPageEventsRef.current.add(eventKey)
        void pageEvent(
          ABHA_ANALYTICS_EVENTS.VIEW_PAGE,
          buildAbhaPageViewProps(ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY, {
            component_name: ABHA_ANALYTICS_PAGE_TYPES.ENTER_PHONE_NUMBER_PAGE,
          })
        )
      }
    }

    if (step === EXISTING_ABHA_MODAL_STEP.Accounts && !isOtpOpen) {
      const eventKey = `${ABHA_ANALYTICS_JOURNEYS.VIEW_ABHA_SCREEN}-${successCurrentState}`

      if (!firedPageEventsRef.current.has(eventKey)) {
        const pageType = successData?.pageType ?? modalOptions?.pageType ?? segmentEventCommonProperties?.page_type
        const variantName =
          successData?.variantName ?? modalOptions?.variantName ?? resolveAbhaSuccessVariantName(successData)
        const analyticsPage = segmentEventCommonProperties?.page

        firedPageEventsRef.current.add(eventKey)
        void pageEvent(
          ABHA_ANALYTICS_EVENTS.VIEW_PAGE,
          buildAbhaPageViewProps(ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY, {
            ...(pageType ? { page_type: pageType } : {}),
            ...(variantName ? { variant_name: variantName } : {}),
            page: typeof analyticsPage === 'string' && analyticsPage ? analyticsPage : window.location.href,
            current_state: successCurrentState,
            component_name: ABHA_ANALYTICS_JOURNEYS.VIEW_ABHA_SCREEN,
            card_count: cardCount,
          })
        )
      }
    }

    const noAbhaFoundEventKey = ABHA_ANALYTICS_VARIANTS.NO_ABHA_FOUND

    if (step !== EXISTING_ABHA_MODAL_STEP.Empty) {
      firedPageEventsRef.current.delete(noAbhaFoundEventKey)
    } else if (!firedPageEventsRef.current.has(noAbhaFoundEventKey)) {
      firedPageEventsRef.current.add(noAbhaFoundEventKey)
      void pageEvent(
        ABHA_ANALYTICS_EVENTS.VIEW_PAGE,
        buildAbhaPageViewProps(ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY, {
          variant_name: ABHA_ANALYTICS_VARIANTS.NO_ABHA_FOUND,
        })
      )
    }

    const phoneVerificationEventKey = ABHA_ANALYTICS_PAGE_TYPES.PHONE_VERIFICATION_SCREEN

    if (!isOtpOpen) {
      firedPageEventsRef.current.delete(phoneVerificationEventKey)
    } else if (!firedPageEventsRef.current.has(phoneVerificationEventKey)) {
      firedPageEventsRef.current.add(phoneVerificationEventKey)
      void pageEvent(
        ABHA_ANALYTICS_EVENTS.VIEW_PAGE,
        buildAbhaPageViewProps(ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY, {
          component_name: ABHA_ANALYTICS_PAGE_TYPES.PHONE_VERIFICATION_SCREEN,
          state_name: ABHA_STATE.SINGLE_CARD,
        })
      )
    }
  }, [
    cardCount,
    isOtpOpen,
    modalOptions?.pageType,
    modalOptions?.variantName,
    open,
    preview,
    segmentEventCommonProperties?.page,
    segmentEventCommonProperties?.page_type,
    step,
    successData,
    successCurrentState,
  ])

  const handleInactiveCardClick = () => {
    if (preview) return

    void trackEvent(
      ABHA_ANALYTICS_EVENTS.TAP_BTN_ENGAGE,
      buildAbhaAnalyticsProps({
        journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
        component_name: ABHA_ANALYTICS_ACTION_TYPES.PROFILE,
      })
    )
  }

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPhone(sanitizeAbhaPhoneNumber(event.target.value))
    if (phoneError) setPhoneError('')
  }

  const handlePhoneBlur = () => {
    if (!phone) return

    if (!isValidPhoneNumber(sanitizedPhone)) {
      setPhoneError(ABHA_HERO_PHONE_ERROR_MESSAGE)
    }
  }

  const openOtpForAccount = (
    nextSession: AbhaSessionState,
    accountIndex: number,
    countdown = ABHA_HERO_MODAL_RESEND_OTP_SECONDS
  ) => {
    if (nextSession?.next_node_id !== AbhaJourneyNodeId.MobileLoginOtpVerificationV2) {
      throw new Error('Unable to verify this ABHA card. Please try again.')
    }

    setSession(nextSession)
    setSelectedAccountIndex(accountIndex)
    setOtp('')
    setOtpError('')
    setOtpSubmitError('')
    setResendCountdown(countdown)
    setIsOtpOpen(true)
  }

  const handleSubmit = () => {
    if (!isFetchEnabled) {
      setPhoneError(ABHA_HERO_PHONE_ERROR_MESSAGE)
      return
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    if (!preview) {
      void trackEvent(
        ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
        buildAbhaAnalyticsProps({
          journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
          cta_text: '',
          action_type: ABHA_ANALYTICS_ACTION_TYPES.PHONE_NUMBER_ENTERED,
        })
      )
    }

    setIsSubmitting(true)
    setPhoneError('')
    setAppDownloadLink(ABHA_HERO_SUCCESS_APP_LINK)

    const runExistingAbhaSearch = async () => {
      try {
        const { session: initialSession } = await startExistingAbhaSearchJourney({
          phone: sanitizedPhone,
          searchParams,
        })

        if (requestId !== requestIdRef.current) return

        setStep(EXISTING_ABHA_MODAL_STEP.Prepare)

        const [searchResult, , deepLink] = await Promise.all([
          searchExistingAbhaAccounts(initialSession),
          wait(ABHA_HERO_MODAL_PREPARE_DURATION_MS),
          preview
            ? Promise.resolve('')
            : fetchDeepLink({
                widgetType: ABHA_SEO_WIDGET,
                mobileNo: sanitizedPhone,
              }),
        ])

        if (requestId !== requestIdRef.current) return

        if (deepLink) setAppDownloadLink(deepLink)
        onFetchAbha?.({ ...searchResult, phone: sanitizedPhone })
        setSession(searchResult.session)

        const memberDetails = searchResult.abhaMemberListDetails ?? []
        const hasAccounts = memberDetails.length > 0
        const isAccountSelectionNode = searchResult.session?.next_node_id === AbhaJourneyNodeId.MobileAccountSelectionV2

        if (memberDetails.length === 1 && isAccountSelectionNode && !memberDetails[0]?.token) {
          const rawAccountIndex = memberDetails[0]?.account_index
          const accountIndex = rawAccountIndex === '' || rawAccountIndex == null ? Number.NaN : Number(rawAccountIndex)

          if (Number.isFinite(accountIndex)) {
            try {
              const nextSession = await selectExistingAbhaAccount(searchResult.session, accountIndex)

              if (requestId !== requestIdRef.current) return

              openOtpForAccount(nextSession, accountIndex)
              setStep(EXISTING_ABHA_MODAL_STEP.Accounts)
              return
            } catch (error) {
              if (requestId !== requestIdRef.current) return
              setActionError(sanitizeAbhaErrorMessage(error, searchResult.session))
            }
          } else {
            setActionError('Unable to select this ABHA card. Please try again.')
          }
        }

        setStep(
          hasAccounts && isAccountSelectionNode ? EXISTING_ABHA_MODAL_STEP.Accounts : EXISTING_ABHA_MODAL_STEP.Empty
        )
      } catch (error) {
        if (requestId !== requestIdRef.current) return

        setStep(EXISTING_ABHA_MODAL_STEP.Phone)
        setPhoneError(sanitizeAbhaErrorMessage(error))
      } finally {
        if (requestId === requestIdRef.current) setIsSubmitting(false)
      }
    }

    runExistingAbhaSearch().catch(() => undefined)
  }

  const handleSelectAccount = (member: AbhaMemberListDetail) => {
    if (!session || isSubmitting || member?.token) return

    const accountIndex = Number(member?.account_index)
    if (!Number.isFinite(accountIndex)) {
      setActionError('Unable to select this ABHA card. Please try again.')
      return
    }

    if (preview) {
      const nextSession = {
        ...session,
        current_node_id: AbhaJourneyNodeId.MobileAccountSelectionV2,
        next_node_id: AbhaJourneyNodeId.MobileLoginOtpVerificationV2,
        abha_member_list_details: session.abha_member_list_details.map(item => ({
          ...item,
          selected: Number(item?.account_index) === accountIndex,
        })),
      }
      openOtpForAccount(nextSession, accountIndex, 0)
      return
    }

    setIsSubmitting(true)
    setActionError('')
    const requestId = requestIdRef.current

    selectExistingAbhaAccount(session, accountIndex)
      .then(nextSession => {
        if (requestId !== requestIdRef.current) return

        openOtpForAccount(nextSession, accountIndex)
      })
      .catch(error => {
        if (requestId === requestIdRef.current) setActionError(sanitizeAbhaErrorMessage(error, session))
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsSubmitting(false)
      })
  }

  const handleViewAbha = () => {
    if (preview) return

    void trackEvent(
      ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
      buildAbhaAnalyticsProps({
        journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
        cta_text: successData?.viewCtaText ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.VIEW_CTA_TEXT,
        action_type: ABHA_ANALYTICS_ACTION_TYPES.VIEW_ABHA_CTA_CLICK,
        current_state: ABHA_STATE.VIEW_ABHA,
      })
    )
  }

  const handleOtpChange = (value: string) => {
    setOtp(value.replace(/\D/g, '').slice(0, ABHA_OTP_LENGTH))
    if (otpError) setOtpError('')
    if (otpSubmitError) setOtpSubmitError('')
  }

  const handleVerifyOtp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!session || selectedAccountIndex === null || !isVerifyOtpEnabled) {
      if (otp.length !== ABHA_OTP_LENGTH) setOtpError(ABHA_HERO_OTP_ERROR_MESSAGE)
      return
    }
    if (!preview) {
      void trackEvent(
        ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
        buildAbhaAnalyticsProps({
          journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
          cta_text: '',
          action_type: ABHA_ANALYTICS_ACTION_TYPES.PHONE_OTP_SUBMIT,
          current_state: ABHA_STATE.POST_VIEW_ABHA_SCREEN,
          component_name: ABHA_ANALYTICS_PAGE_TYPES.PHONE_OTP_SCREEN,
          state_name: ABHA_STATE.SINGLE_CARD,
        })
      )
    }

    setIsSubmitting(true)
    setOtpError('')
    setOtpSubmitError('')
    const requestId = requestIdRef.current

    verifyExistingAbhaLoginOtp(session, selectedAccountIndex, otp)
      .then(nextSession => {
        if (requestId !== requestIdRef.current) return

        setSession(nextSession)
        setIsOtpOpen(false)
        setSelectedAccountIndex(null)
        setOtp('')
        setActionError('')
      })
      .catch(error => {
        if (requestId !== requestIdRef.current) return

        const errorMessage = sanitizeAbhaErrorMessage(error, session)
        if (isAbhaOtpInvalidError(error)) {
          setOtpError(errorMessage)
          setResendCountdown(0)
          return
        }

        setOtpSubmitError(errorMessage)
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsSubmitting(false)
      })
  }

  const handleResendOtp = () => {
    if (!session || selectedAccountIndex === null || resendCountdown > 0 || isSubmitting) return

    if (!preview) {
      void trackEvent(
        ABHA_ANALYTICS_EVENTS.TAP_BTN_ENGAGE,
        buildAbhaAnalyticsProps({
          journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
          component_name: ABHA_ANALYTICS_ACTION_TYPES.RESEND_OTP,
        })
      )
    }

    setIsSubmitting(true)
    setOtpError('')
    setOtpSubmitError('')
    const requestId = requestIdRef.current

    resendExistingAbhaLoginOtp(session, selectedAccountIndex)
      .then(nextSession => {
        if (requestId !== requestIdRef.current) return

        setSession(nextSession)
        setOtp('')
        setResendCountdown(ABHA_HERO_MODAL_RESEND_OTP_SECONDS)
      })
      .catch(error => {
        if (requestId === requestIdRef.current) setOtpSubmitError(sanitizeAbhaErrorMessage(error, session))
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsSubmitting(false)
      })
  }

  const handleDownload = (member: AbhaMemberListDetail) => {
    if (!session) return

    if (!preview) {
      const pageType = successData?.pageType ?? modalOptions?.pageType ?? segmentEventCommonProperties?.page_type
      const variantName =
        successData?.variantName ?? modalOptions?.variantName ?? resolveAbhaSuccessVariantName(successData)
      const analyticsPage = segmentEventCommonProperties?.page

      void trackEvent(
        ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
        buildAbhaAnalyticsProps({
          journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
          cta_text: '',
          action_type: ABHA_ANALYTICS_ACTION_TYPES.ABHA_CARD_DOWNLOAD,
          page_type: pageType,
          page: typeof analyticsPage === 'string' && analyticsPage ? analyticsPage : window.location.href,
          variant_name: variantName,
          current_state: ABHA_STATE.DOWNLOAD_ABHA,
        })
      )
    }

    try {
      downloadAbhaCardAsset(session, {
        cardVariant: 'showAbhaNumber',
        accountIndex: Number(member?.account_index),
      })
      setActionError('')
    } catch (error) {
      setActionError(sanitizeAbhaErrorMessage(error, session))
    }
  }

  const handleDownloadApp = () => {
    if (preview) return

    const pageType = successData?.pageType ?? modalOptions?.pageType ?? segmentEventCommonProperties?.page_type
    const variantName =
      successData?.variantName ?? modalOptions?.variantName ?? resolveAbhaSuccessVariantName(successData)
    const analyticsPage = segmentEventCommonProperties?.page

    void trackEvent(
      ABHA_ANALYTICS_EVENTS.TAP_BTN_ACKO_APP,
      buildAbhaAnalyticsProps({
        component_name: ABHA_ANALYTICS_JOURNEYS.APP_DOWNLOAD_CLICK,
        page_type: pageType,
        page: typeof analyticsPage === 'string' && analyticsPage ? analyticsPage : window.location.href,
        ...(variantName ? { variant_name: variantName } : {}),
        current_state: ABHA_STATE.DOWNLOAD_ABHA,
        journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
      })
    )

    window.location.href = appDownloadLink
  }

  const handleTryDifferentNumber = () => {
    if (!preview) {
      void trackEvent(
        ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
        buildAbhaAnalyticsProps({
          journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
          cta_text: ABHA_HERO_WIDGET_DEFAULT_DATA.ALREADY_HAVE_ABHA_TEXT,
          funnel: 'entry',
          variant_name: ABHA_ANALYTICS_VARIANTS.NO_ABHA_FOUND,
        })
      )
    }

    requestIdRef.current += 1
    setStep(EXISTING_ABHA_MODAL_STEP.Phone)
    setPhone('')
    setPhoneError('')
    setSession(null)
    setActionError('')
    setAppDownloadLink(ABHA_HERO_SUCCESS_APP_LINK)
  }

  const handleCreateAbha = () => {
    if (!preview) {
      void trackEvent(
        ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
        buildAbhaAnalyticsProps({
          journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
          cta_text: ABHA_HERO_WIDGET_DEFAULT_DATA.CTA_TEXT,
          funnel: 'entry',
          variant_name: ABHA_ANALYTICS_VARIANTS.NO_ABHA_FOUND,
        })
      )
    }

    onCreateAbha?.()
  }

  const renderMainContent = () => {
    if (step === EXISTING_ABHA_MODAL_STEP.Prepare) {
      return (
        <AbhaHeroPrepareStep
          data={{
            imageSrc: ABHA_HERO_EXISTING_ABHA_PREPARE_STEP_DATA.IMAGE_SRC,
            title: ABHA_HERO_EXISTING_ABHA_PREPARE_STEP_DATA.TITLE,
            ...modalOptions?.existingAbhaPrepareStepData,
          }}
          isMobile={isMobile}
        />
      )
    }

    if (step === EXISTING_ABHA_MODAL_STEP.Accounts && session?.abha_member_list_details?.length) {
      return (
        <AbhaHeroExistingAbhaSuccessStep
          data={successData}
          phone={sanitizedPhone}
          members={session.abha_member_list_details}
          isMobile={isMobile}
          errorMessage={actionError}
          appDownloadLink={appDownloadLink}
          onSelect={handleSelectAccount}
          onDownload={handleDownload}
          onDownloadApp={handleDownloadApp}
          onInactiveCardClick={handleInactiveCardClick}
          onViewAbha={handleViewAbha}
          ctaAnalyticsProps={successCtaAnalyticsProps}
        />
      )
    }

    if (step === EXISTING_ABHA_MODAL_STEP.Empty) {
      return (
        <div className="abha-hero-existing-empty">
          <Image
            src={emptyData.imageSrc || ABHA_HERO_EXISTING_ABHA_EMPTY_STEP_DATA.IMAGE_SRC}
            alt=""
            width={144}
            height={144}
            className="abha-hero-existing-empty__image"
            aria-hidden
          />
          <h2>{emptyData.title}</h2>
          <div className="abha-hero-existing-empty__actions">
            <Button type="button" variant="primary" size={isMobile ? 'sm' : 'md'} onClick={handleTryDifferentNumber}>
              {emptyData.primaryCtaText}
            </Button>
            {onCreateAbha && (
              <Button type="button" variant="ghost" size={isMobile ? 'sm' : 'md'} onClick={handleCreateAbha}>
                {emptyData.secondaryCtaText}
              </Button>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="abha-hero-modal__content abha-hero-modal__content--form abha-hero-existing-modal__content">
        <div className="abha-hero-modal__scroll abha-hero-existing-modal__scroll">
          <div className="abha-hero-modal__intro abha-hero-existing-modal__intro">
            <Typography variant="heading-lg" as="h2" color="primary" className="abha-hero-modal__intro-title">
              {stepData.title}
            </Typography>
            <Typography scale="sm" emphasis="normal" color="secondary" className="abha-hero-modal__intro-description">
              {stepData.description}
            </Typography>
          </div>

          <TextInput
            className="abha-hero-modal__phone-input abha-hero-existing-modal__phone-input"
            label={isMobile ? stepData.inputLabel : undefined}
            inputType="phone"
            size="lg"
            placeholder={stepData.inputPlaceholder}
            value={phone}
            maxLength={ABHA_HERO_PHONE_MAX_LENGTH}
            error={Boolean(phoneError)}
            errorText={phoneError || undefined}
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
            autoComplete="tel"
          />
        </div>

        <div className="abha-hero-modal__footer abha-hero-existing-modal__footer">
          <div className="abha-hero-modal__footer-disclaimer">
            <ModalDisclaimer text={stepData.disclaimerText} iconSrc={stepData.disclaimerIconSrc} />
          </div>

          <div className="abha-hero-modal__footer-action">
            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              disabled={!isFetchEnabled || isSubmitting}
              loading={isSubmitting}
              onClick={handleSubmit}>
              {stepData.ctaText}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const maskedPhoneNumber = session?.abha_member_list_details?.find(
    member => Number(member?.account_index) === selectedAccountIndex
  )?.masked_phone_number
  const otpData = {
    title: ABHA_HERO_EXISTING_ABHA_OTP_STEP_DATA.TITLE,
    description: ABHA_HERO_EXISTING_ABHA_OTP_STEP_DATA.DESCRIPTION,
    otpLabel: ABHA_HERO_EXISTING_ABHA_OTP_STEP_DATA.OTP_LABEL,
    ctaText: ABHA_HERO_EXISTING_ABHA_OTP_STEP_DATA.CTA_TEXT,
    ...modalOptions?.existingAbhaOtpStepData,
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size={isMobile ? 'full' : 'md'}
      className={dialogClassName}
      dismissible={false}>
      {step !== EXISTING_ABHA_MODAL_STEP.Prepare && (
        <div className="acko-dialog-header">
          <div className="acko-dialog-heading" aria-hidden />
          <AbhaHeroModalCloseButton onClick={onClose} size={isMobile ? 'lg' : 'md'} />
        </div>
      )}
      {renderMainContent()}

      {isOtpOpen && (
        <div className="abha-hero-existing-otp-overlay">
          <section className="abha-hero-existing-otp-modal" role="dialog" aria-modal="true" aria-label={otpData.title}>
            <div className="acko-dialog-header">
              <div className="acko-dialog-heading" aria-hidden />
              <AbhaHeroModalCloseButton onClick={() => setIsOtpOpen(false)} size={isMobile ? 'lg' : 'md'} />
            </div>
            <AbhaHeroOtpStep
              data={otpData}
              isMobile={isMobile}
              maskedPhoneNumber={maskedPhoneNumber}
              phoneNumber={sanitizedPhone}
              variant="mobile"
              otp={otp}
              otpError={otpError}
              submitError={otpSubmitError}
              prepareError=""
              resendCountdown={resendCountdown}
              isVerifyOtpEnabled={isVerifyOtpEnabled}
              isSubmitting={isSubmitting}
              canResendOtp={resendCountdown === 0 && !isSubmitting}
              onOtpChange={handleOtpChange}
              onResendOtp={handleResendOtp}
              onSubmit={handleVerifyOtp}
            />
          </section>
        </div>
      )}
    </Dialog>
  )
}
